/**
 * The client Day-0 decision table (agreement-first onboarding, T01) — PURE: no React, no I/O, no storage.
 *
 * `Day0LaunchScreen`, the refetch policy (ADR-015) and the import lock (ADR-014) all read the same
 * derivation, which is what keeps them from disagreeing about which stage is "current", whether the
 * client is being held, and what to say.
 *
 * Two rules are load-bearing and each was learned the hard way:
 *  1. `heldReason` is keyed on `reason`, NEVER on `state`. `ADVANCING` rides a READY step (the only reason
 *     that does — README §3, Rev 5.3) while every other reason is BLOCKED; keying on `reason` is correct
 *     under both, so a future reason on a READY branch keeps working.
 *  2. `heldReason` is null whenever ANY stage is NEEDED. A grandfathered org has an agreement step that is
 *     BLOCKED with `BEING_SET_UP` *while* `entity_data` is NEEDED — it is working normally and must not be
 *     shown the holding screen.
 */
import type { AgreementStepReason } from '@/gql/graphql'
import type { ClientActivationState } from '@/lib/hooks/useMyActivationState'

export type ClientStage = ClientActivationState['stages'][number]

/** `needed` — some stage has a live action; `held` — nothing is actionable and the agreement step says why;
 *  `fallback` — nothing is actionable and nothing says why (must still render something honest). */
export type Day0Mode = 'needed' | 'held' | 'fallback'

export type Day0Cta =
  /** The agreement sign action. Only ever produced with a navigable `actionId` (the dead-CTA guarantee). */
  | { kind: 'sign'; label: string; agreementId: string }
  /** Legacy behaviour for every other NEEDED stage: the header CTA opens the new-entity form. */
  | { kind: 'entities-new'; label: string }
  | null

export interface Day0View {
  neededStage: ClientStage | undefined
  agreementStep: ClientStage | undefined
  /** `neededStage ? null : (agreementStep?.reason ?? null)` — see the file header. */
  heldReason: AgreementStepReason | null
  mode: Day0Mode
  cta: Day0Cta
}

/** Holds that resolve without anyone acting on the client's behalf. */
export const SELF_RESOLVING_REASONS: ReadonlySet<AgreementStepReason> = new Set<AgreementStepReason>([
  'BEING_SET_UP',
  'AWAITING_COUNTERSIGNATURE',
  'ADVANCING',
])

export function deriveDay0View(state: Pick<ClientActivationState, 'stages'>): Day0View {
  const neededStage = state.stages.find((s) => s.state === 'NEEDED')
  const agreementStep = state.stages.find((s) => s.key === 'agreement')
  const heldReason = neededStage ? null : (agreementStep?.reason ?? null)
  const mode: Day0Mode = neededStage ? 'needed' : heldReason ? 'held' : 'fallback'

  let cta: Day0Cta = null
  if (neededStage?.primaryAction) {
    if (neededStage.key === 'agreement') {
      // NEEDED without a navigable actionId renders no CTA at all: a sign button must never point at nothing.
      cta = neededStage.actionId
        ? { kind: 'sign', label: neededStage.primaryAction, agreementId: neededStage.actionId }
        : null
    } else {
      cta = { kind: 'entities-new', label: neededStage.primaryAction }
    }
  }
  return { neededStage, agreementStep, heldReason, mode, cta }
}

/**
 * The stepper caption ("Step {position} of {total} · {label}") — which stage it names.
 *
 *  • a NEEDED stage → that stage, printed with the GIVEN `currentStage` (BE's machine ordinal) exactly as before;
 *  • HELD (nothing NEEDED, the agreement carries a `reason`) → the AGREEMENT step, by its own position. Not `stages[currentStage - 1]`:
 *    since AFO-T35 BE serves a held agreement at `currentStage: 1` (`client_created` is READY — "a record, not a task"), so the ordinal
 *    points at the stage the stepper already draws as completed ("Step 1 of 5 · Client created" beside a ✓ on Client created, while the
 *    current-stage card says Agreement). Same condition as `heldReason` — this derives from `deriveDay0View`, so the two cannot drift;
 *  • otherwise (the no-reason fallback) → `stages[currentStage - 1]`, unchanged.
 */
export function stepCaption(
  stages: ClientActivationState['stages'],
  currentStage: number,
): { position: number; label: string } {
  const view = deriveDay0View({ stages })
  if (view.neededStage) return { position: currentStage, label: view.neededStage.label }
  const held = view.heldReason ? view.agreementStep : undefined
  if (held) return { position: stages.indexOf(held) + 1, label: held.label }
  return { position: currentStage, label: stages[currentStage - 1]?.label ?? '' }
}

/**
 * ADR-015 — how often to refetch the activation query while a hold is DISPLAYED.
 * 10 s for holds that resolve on their own · 60 s for operator-resolved holds and the no-reason fallback ·
 * `false` while a stage is NEEDED or the status is not IN_PROGRESS. There is deliberately no client timeout:
 * `ADVANCING` is "usually milliseconds and occasionally forever" (README §5b).
 */
export function heldPollIntervalMs(state: ClientActivationState | undefined): number | false {
  if (!state || state.status !== 'IN_PROGRESS') return false
  const view = deriveDay0View(state)
  if (view.mode === 'needed') return false
  if (view.mode === 'held' && view.heldReason && SELF_RESOLVING_REASONS.has(view.heldReason)) return 10_000
  return 60_000
}

export interface HeldCopy {
  heading: string
  body: string
}

const SETTING_UP: HeldCopy = {
  heading: 'Your agreement is being set up',
  body: "We're preparing your agreement. You'll be able to review and sign it here as soon as it's ready — there's nothing you need to do right now.",
}

/**
 * Held / fallback copy. Second person (this is the client's own screen), no `error` / `failed` / `support`
 * vocabulary: the hold is temporary and operator-resolved, not a fault the client has to report.
 * Headings are pinned by FE-AC-C1–C6; bodies are FE-proposed and owner-approved.
 */
export const HELD_COPY: Record<AgreementStepReason | 'FALLBACK', HeldCopy> = {
  PAPERING_FAILED: SETTING_UP,
  ORG_UNIDENTIFIABLE: SETTING_UP,
  BEING_SET_UP: SETTING_UP,
  AWAITING_COUNTERSIGNATURE: {
    heading: 'Signed — waiting for countersignature',
    body: "You've signed. Once the other party countersigns, you'll move on to adding your entity data.",
  },
  ADVANCING: {
    heading: 'Agreement complete — setting up your next step',
    body: "Your agreement is fully signed. We're opening the next step for you now.",
  },
  FALLBACK: {
    heading: 'Setting up your account',
    body: "We're getting your account ready. This page updates on its own — there's nothing you need to do right now.",
  },
}

/** A future BE reason the FE has not seen yet must degrade to the fallback copy, never to `undefined`. */
export function heldCopyFor(reason: AgreementStepReason | null): HeldCopy {
  return (reason ? HELD_COPY[reason] : undefined) ?? HELD_COPY.FALLBACK
}
